/**
 * Relatórios (área «Relatórios», só gestores): quantidades e valores das
 * propostas por mês, vendedor, estado, módulo e origem, com filtro de datas,
 * e o seguimento das propostas do site no Odoo (FollowupSection).
 * Os dados vêm de `GET reporting/proposals` (uma linha por proposta com as
 * datas dos eventos); as contas fazem-se aqui (src/lib/reporting.ts).
 */
import { useEffect, useMemo, useState } from "react"
import { Download, Loader2 } from "lucide-react"
import {
  Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { formatEur } from "@/lib/format"
import { LEAD_ORIGINS, leadOriginOption } from "@/lib/lead-origin"
import {
  KIND_LABEL, NO_ORIGIN, PERIOD_LABEL, buildReport, monthLabel, presetRange, sellerMonthCsv,
  type Counts, type PeriodPreset, type ReportFilters, type ReportKind, type ReportRow,
} from "@/lib/reporting"
import { cn } from "@/lib/utils"
import { FollowupSection } from "./FollowupSection"

const ALL = "__all__"
const SERIES = { created: "var(--series-1)", sent: "var(--series-2)", won: "var(--series-3)" }
const SERIES_LABEL = { created: "Criadas", sent: "Enviadas", won: "Ganhas" }

const pct = (v: number | null) => (v == null ? "—" : `${Math.round(v * 100)}%`)
const int = (n: number) => n.toLocaleString("pt-PT")
const eur0 = (n: number) => n.toLocaleString("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
const originLabel = (k: string) => (k === NO_ORIGIN ? "Sem origem" : leadOriginOption(k)?.label ?? k)

type Metric = keyof Counts
const METRICS: Array<{ key: Metric; label: string }> = [
  { key: "created", label: "Criadas" },
  { key: "draft", label: "Em rascunho" },
  { key: "sent", label: "Enviadas" },
  { key: "won", label: "Ganhas" },
  { key: "lost", label: "Perdidas" },
  { key: "wonValue", label: "Valor ganho" },
]

export interface ReportingData {
  rows: ReportRow[]
}

export function Reporting({ shell, load, loadFollowup }: {
  shell: AppShell | null
  load?: () => Promise<ReportingData>
  /** Pré-visualização/testes: dados do seguimento sem o Odoo. */
  loadFollowup?: Parameters<typeof FollowupSection>[0]["load"]
}) {
  const [data, setData] = useState<ReportingData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [preset, setPreset] = useState<PeriodPreset>("last_6")
  const [range, setRange] = useState(() => presetRange("last_6"))
  const [seller, setSeller] = useState<string>(ALL)
  const [kind, setKind] = useState<string>(ALL)
  const [origin, setOrigin] = useState<string>(ALL)
  const [generatedBy, setGeneratedBy] = useState<string>(ALL)
  const [metric, setMetric] = useState<Metric>("sent")

  useEffect(() => {
    if (!shell) return
    const fetcher = load ?? (async () => {
      const res = await shell.client.get<{ success: boolean; data?: ReportingData; error?: string }>("reporting/proposals")
      if (!res.success || !res.data) throw new Error(res.error || "Falha a carregar os relatórios")
      return res.data
    })
    fetcher().then(setData).catch((e) => setError(e instanceof Error ? e.message : "Falha"))
  }, [shell, load])

  const filters: ReportFilters = {
    from: range.from,
    to: range.to,
    seller: seller === ALL ? null : seller,
    kind: kind === ALL ? null : (kind as ReportKind),
    origin: origin === ALL ? null : origin,
    generatedBy: generatedBy === ALL ? null : (generatedBy as "user" | "ai"),
  }
  const report = useMemo(() => (data ? buildReport(data.rows, filters) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, range.from, range.to, seller, kind, origin, generatedBy])

  const sellers = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of data?.rows ?? []) if (r.seller_id) m.set(r.seller_id, r.seller_name ?? "Sem nome")
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "pt"))
  }, [data])

  function choosePreset(p: PeriodPreset) {
    setPreset(p)
    if (p !== "custom") setRange(presetRange(p))
  }

  function exportCsv() {
    if (!report) return
    const label = METRICS.find((m) => m.key === metric)!.label
    const blob = new Blob(["﻿" + sellerMonthCsv(report, metric, label)], { type: "text/csv;charset=utf-8" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = `relatorio-${metric}-${range.from}_${range.to}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  if (error) return <p className="text-sm text-destructive">{error}</p>
  if (!report) {
    return <div className="flex justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
  }
  const t = report.totals

  return (
    <div className="space-y-6">
      {/* Filtros — uma linha por cima de tudo. */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
        <FilterSelect label="Período" value={preset} onChange={(v) => choosePreset(v as PeriodPreset)}
          options={(Object.keys(PERIOD_LABEL) as PeriodPreset[]).map((k) => ({ value: k, label: PERIOD_LABEL[k] }))} />
        <div className="space-y-1">
          <Label className="text-xs">De</Label>
          <Input type="date" className="h-9 w-[9.5rem]" value={range.from} max={range.to}
            onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })) }} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Até</Label>
          <Input type="date" className="h-9 w-[9.5rem]" value={range.to} min={range.from}
            onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })) }} />
        </div>
        <FilterSelect label="Vendedor" value={seller} onChange={setSeller}
          options={[{ value: ALL, label: "Todos" }, ...sellers.map((s) => ({ value: s.id, label: s.name }))]} />
        <FilterSelect label="Módulo" value={kind} onChange={setKind}
          options={[{ value: ALL, label: "Todos" }, ...(Object.keys(KIND_LABEL) as ReportKind[]).map((k) => ({ value: k, label: KIND_LABEL[k] }))]} />
        <FilterSelect label="Origem" value={origin} onChange={setOrigin}
          options={[{ value: ALL, label: "Todas" }, ...LEAD_ORIGINS.map((o) => ({ value: o.key, label: o.label })), { value: NO_ORIGIN, label: "Sem origem" }]} />
        <FilterSelect label="Origem da proposta" value={generatedBy} onChange={setGeneratedBy}
          options={[{ value: ALL, label: "Todas" }, { value: "ai", label: "Geradas por IA" }, { value: "user", label: "Comerciais" }]} />
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Criadas" value={int(t.created)} hint={`${int(t.draft)} ainda em rascunho`} />
        <Kpi label="Enviadas" value={int(t.sent)} />
        <Kpi label="Ganhas" value={int(t.won)} hint={`Conversão ${pct(t.conversion)} das enviadas`} />
        <Kpi label="Valor ganho" value={eur0(t.wonValue)} hint="sem IVA" />
        <Kpi label="Ticket médio" value={t.avgTicket == null ? "—" : eur0(t.avgTicket)}
          hint={t.avgDaysToWin == null ? undefined : `${Math.round(t.avgDaysToWin)} dias até ganhar`} />
        <Kpi label="Em aberto" value={eur0(t.openValue)} hint={`${int(t.openCount)} enviadas/em revisão`} />
      </div>

      <FollowupSection
        shell={shell}
        from={range.from}
        to={range.to}
        kind={kind === ALL ? null : kind}
        seller={seller === ALL ? null : seller}
        pageOrigin={generatedBy === ALL ? null : (generatedBy as "user" | "ai")}
        load={loadFollowup}
      />

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Por mês</CardTitle></CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={report.byMonth} barGap={2} barCategoryGap="22%" margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} />
              <Tooltip cursor={{ fill: "var(--viz-grid)", opacity: 0.5 }} contentStyle={tooltipStyle} />
              <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
              {(["created", "sent", "won"] as const).map((k) => (
                <Bar key={k} dataKey={k} name={SERIES_LABEL[k]} fill={SERIES[k]} radius={[4, 4, 0, 0]} isAnimationActive={false} maxBarSize={22} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Valor ganho por mês <span className="text-xs font-normal text-muted-foreground">(sem IVA)</span></CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={report.byMonth} margin={{ top: 18, right: 8, left: 4, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))} />
                <Tooltip cursor={{ fill: "var(--viz-grid)", opacity: 0.5 }} contentStyle={tooltipStyle} formatter={(v: number) => [formatEur(v), "Valor ganho"]} />
                <Bar dataKey="wonValue" name="Valor ganho" fill={SERIES.won} radius={[4, 4, 0, 0]} isAnimationActive={false} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Estado atual <span className="text-xs font-normal text-muted-foreground">(das criadas no período)</span></CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={report.byStatus} layout="vertical" margin={{ top: 4, right: 36, left: 8, bottom: 0 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="label" tickLine={false} axisLine={false} fontSize={12} width={84} />
                <Tooltip cursor={{ fill: "var(--viz-grid)", opacity: 0.5 }} contentStyle={tooltipStyle} formatter={(v: number) => [int(v), "Propostas"]} />
                <Bar dataKey="count" name="Propostas" fill={SERIES.created} radius={[0, 4, 4, 0]} isAnimationActive={false} maxBarSize={22}>
                  <LabelList dataKey="count" position="right" fontSize={12} className="fill-foreground" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Por módulo</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={report.byKind} barGap={2} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} />
                <Tooltip cursor={{ fill: "var(--viz-grid)", opacity: 0.5 }} contentStyle={tooltipStyle} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                {(["created", "sent", "won"] as const).map((k) => (
                  <Bar key={k} dataKey={k} name={SERIES_LABEL[k]} fill={SERIES[k]} radius={[4, 4, 0, 0]} isAnimationActive={false} maxBarSize={22} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Por origem</CardTitle></CardHeader>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Origem</TableHead>
                  <TableHead className="text-right">Criadas</TableHead>
                  <TableHead className="text-right">Ganhas</TableHead>
                  <TableHead className="text-right">Conversão</TableHead>
                  <TableHead className="text-right">Valor ganho</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.byOrigin.map((o) => {
                  const max = Math.max(1, ...report.byOrigin.map((x) => x.created))
                  return (
                    <TableRow key={o.origin}>
                      <TableCell>
                        <div className="text-sm">{originLabel(o.origin)}</div>
                        <div className="mt-1 h-1.5 rounded-full bg-muted">
                          <div className="h-1.5 rounded-full" style={{ width: `${(o.created / max) * 100}%`, background: SERIES.created }} />
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{int(o.created)}</TableCell>
                      <TableCell className="text-right tabular-nums">{int(o.won)}</TableCell>
                      <TableCell className="text-right tabular-nums">{pct(o.conversion)}</TableCell>
                      <TableCell className="text-right tabular-nums">{eur0(o.wonValue)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Vendedores — o resumo e o detalhe por mês. */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Vendedores</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendedor</TableHead>
                <TableHead className="text-right">Criadas</TableHead>
                <TableHead className="text-right">Em rascunho</TableHead>
                <TableHead className="text-right">Enviadas</TableHead>
                <TableHead className="text-right">Ganhas</TableHead>
                <TableHead className="text-right">Perdidas</TableHead>
                <TableHead className="text-right">Conversão</TableHead>
                <TableHead className="text-right">Valor ganho</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.bySeller.map((s) => (
                <TableRow key={s.id} className="cursor-pointer" onClick={() => setSeller(seller === s.id ? ALL : s.id)}>
                  <TableCell className={cn("font-medium", seller === s.id && "text-accent")}>{s.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{int(s.created)}</TableCell>
                  <TableCell className="text-right tabular-nums">{int(s.draft)}</TableCell>
                  <TableCell className="text-right tabular-nums">{int(s.sent)}</TableCell>
                  <TableCell className="text-right tabular-nums">{int(s.won)}</TableCell>
                  <TableCell className="text-right tabular-nums">{int(s.lost)}</TableCell>
                  <TableCell className="text-right tabular-nums">{pct(s.conversion)}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur0(s.wonValue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
          <CardTitle className="text-base">Por vendedor e mês</CardTitle>
          <div className="flex flex-wrap items-center gap-1">
            {METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMetric(m.key)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs",
                  metric === m.key ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:bg-muted",
                )}
              >
                {m.label}
              </button>
            ))}
            <Button variant="outline" size="sm" className="ml-1 h-7" onClick={exportCsv}>
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-card">Vendedor</TableHead>
                {report.months.map((m) => <TableHead key={m} className="text-right">{monthLabel(m)}</TableHead>)}
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.bySeller.map((s) => {
                const max = Math.max(1, ...report.bySeller.flatMap((x) => report.months.map((m) => x.months[m]![metric])))
                return (
                  <TableRow key={s.id}>
                    <TableCell className="sticky left-0 bg-card font-medium">{s.name}</TableCell>
                    {report.months.map((m) => {
                      const v = s.months[m]![metric]
                      return (
                        <TableCell key={m} className="text-right tabular-nums"
                          style={v > 0 ? { background: `color-mix(in oklab, var(--series-1) ${Math.round(8 + (v / max) * 32)}%, transparent)` } : undefined}>
                          {v === 0 ? <span className="text-muted-foreground">·</span> : metric === "wonValue" ? eur0(v) : int(v)}
                        </TableCell>
                      )
                    })}
                    <TableCell className="text-right font-semibold tabular-nums">{metric === "wonValue" ? eur0(s[metric]) : int(s[metric])}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="sticky left-0 bg-muted/50 font-medium">Total</TableCell>
                {report.byMonth.map((mm) => (
                  <TableCell key={mm.month} className="text-right tabular-nums">{metric === "wonValue" ? eur0(mm[metric]) : int(mm[metric])}</TableCell>
                ))}
                <TableCell className="text-right font-semibold tabular-nums">{metric === "wonValue" ? eur0(t[metric]) : int(t[metric])}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">
        Cada número conta no mês em que aconteceu: criada, enviada (1.º envio por email ou passagem a «enviada»), ganha ou perdida.
        «Em rascunho» são as criadas no período que continuam em rascunho. Valores sem IVA.
      </p>
    </div>
  )
}

const tooltipStyle = {
  background: "var(--popover)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  fontSize: 12,
  color: "var(--popover-foreground)",
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}

function FilterSelect({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }>
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 min-w-[10rem]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}
